import { Link } from "@inertiajs/react";
import { Badge, Card, Flex, Heading, Table, Tabs, Text } from "@radix-ui/themes";
import { badgeColors } from "@/shared/constants/constants";
import WithdrawFromEventButton from "@/shared/WithdrawFromEventButton";
import UserPageShell from "@/shared/users/UserPageShell";
import type {
  UsersBrandedApplicationsApplication,
  UsersBrandedApplicationsProps,
} from "@/types/users_branded_applications";

// "Your applications" on a branded site: only the brand's events, beside what
// you apply with, and one deliberate way out to the rest on driftables.net —
// the wireframe's screen 6 (tickets/white-label-media-application-flow.md).
// Driftables itself keeps users/applications.
export default function BrandedApplications({
  name,
  owner_kind,
  upcoming,
  past,
  elsewhere_count,
  driftables_url,
  apply_with,
}: UsersBrandedApplicationsProps) {
  const title = `Your applications ${owner_kind === "Club" ? "with" : "at"} ${name}`;

  return (
    <UserPageShell title={title} wide>
      <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
        <section className="flex min-w-0 flex-col gap-4 lg:flex-[3]">
          <Heading size="7">{title}</Heading>

          <Tabs.Root defaultValue={upcoming.length > 0 || past.length === 0 ? "upcoming" : "past"}>
            <Tabs.List>
              <Tabs.Trigger value="upcoming">Upcoming ({upcoming.length})</Tabs.Trigger>
              <Tabs.Trigger value="past">Past ({past.length})</Tabs.Trigger>
            </Tabs.List>
            <Tabs.Content value="upcoming">
              <ApplicationsTable
                applications={upcoming}
                empty={`No upcoming applications. Find an event from ${name} to apply to.`}
              />
            </Tabs.Content>
            <Tabs.Content value="past">
              <ApplicationsTable applications={past} empty="Nothing here yet." />
            </Tabs.Content>
          </Tabs.Root>

          {elsewhere_count > 0 && (
            <Card>
              <Flex justify="between" align="center" gap="3" wrap="wrap">
                <Text size="2">
                  You have {elsewhere_count} more {elsewhere_count === 1 ? "application" : "applications"} to other
                  events.
                </Text>
                {/* A plain link: it's another site. */}
                <Text size="2" weight="bold">
                  <a href={driftables_url} className="text-accent11 hover:underline">
                    See everything on Driftables
                  </a>
                </Text>
              </Flex>
            </Card>
          )}
        </section>

        <aside aria-labelledby="apply-with" className="flex flex-col gap-3 lg:flex-[2]">
          <Heading as="h2" size="4" id="apply-with">
            What you apply with
          </Heading>
          <Card>
            <ul className="flex flex-col divide-y divide-gray4">
              {apply_with.map((row) => (
                <li key={row.label} className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
                  <Flex direction="column">
                    <Text weight="bold" size="2">
                      {row.label}
                    </Text>
                    <Text size="2" color="gray">
                      {row.detail}
                    </Text>
                  </Flex>
                  <Link href={row.href} className="text-sm font-semibold text-accent11 hover:underline">
                    {row.action}
                  </Link>
                </li>
              ))}
            </ul>
          </Card>
          <Text size="1" color="gray">
            They're part of your Driftables account, so they work at every track.
          </Text>
        </aside>
      </div>
    </UserPageShell>
  );
}

function ApplicationsTable({
  applications,
  empty,
}: {
  applications: UsersBrandedApplicationsApplication[];
  empty: string;
}) {
  if (applications.length === 0) {
    return (
      <Text as="p" color="gray" mt="4">
        {empty}
      </Text>
    );
  }

  return (
    <div className="mt-2 overflow-x-auto">
      <Table.Root>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell>Event</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Applied as</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>
              <span className="sr-only">Actions</span>
            </Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {applications.map((application) => (
            <Table.Row key={application.id} align="center">
              <Table.RowHeaderCell>
                <Flex direction="column">
                  <Link href={`/events/${application.event_id}`} className="font-semibold hover:underline">
                    {application.event_name}
                  </Link>
                  <Text size="1" color="gray">
                    {application.event_date} · {application.track_name}
                  </Text>
                </Flex>
              </Table.RowHeaderCell>
              <Table.Cell>
                <Flex direction="column">
                  <Text size="2">{application.applied_as}</Text>
                  <Text size="1" color="gray">
                    {application.applied_as_kind}
                  </Text>
                </Flex>
              </Table.Cell>
              <Table.Cell>
                <Badge variant="soft" color={badgeColors[application.status]} style={{ textTransform: "capitalize" }}>
                  {application.status}
                </Badge>
              </Table.Cell>
              <Table.Cell justify="end">
                {application.withdrawable && <WithdrawFromEventButton application={application} />}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </div>
  );
}
